import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startChallenge, verifyChallenge, isErr, type Principal } from '../auth/nju';
import { loginPassword, fetchMe } from '../auth/photozen';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';
import { renderStroke } from '../canvas/brushes';
import type { Point } from '@blackboard/shared';
import { AlertIcon, LogoMark } from '../ui/icons';

/* ------------------------------------------------------------------ */
/*  全屏黑板沉浸式登录：粉笔字 + 虚线粉笔控件 + 真实涂鸦作背景痕迹      */
/* ------------------------------------------------------------------ */

type Tab = 'nanna' | 'pill';
type NannaStep = 'identify' | 'verify' | 'guide';


const TAB_STORAGE_KEY = 'bb_login_tab_v2';
const BIND_ERROR_CODES = new Set(['OAUTH_USER_NOT_FOUND', 'WECHAT_USER_INACTIVE']);

const CHALK_TEXT_SHADOW = '0 0 2px rgba(255,255,255,0.22)';

export default function Login() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
  const setAuth = useAuth((s) => s.setAuth);
  const pushToast = useToast((s) => s.push);

  const [tab, setTab] = useState<Tab>(() => {
    const q = searchParams.get('tab');
    if (q === 'pill' || q === 'nanna') return q;
    if (typeof localStorage !== 'undefined' && localStorage.getItem(TAB_STORAGE_KEY) === 'pill') return 'pill';
    return 'nanna';
  });

  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* ignore */ }
  }, [tab]);

  /** Post-auth navigation with admin-route guard. */
  function navigateWithGuard(me: { is_admin: boolean }) {
    if (safeNext.startsWith('/admin') && !me.is_admin) {
      pushToast('info', '当前账号不是管理员，已为你打开画板。', 3000);
      nav('/', { replace: true });
      return;
    }
    nav(safeNext, { replace: true });
  }

  /** 药丸链路：token → /api/me → 入驻 → 跳转。返回错误文案或 null。 */
  async function afterPillAuth(accessToken: string): Promise<string | null> {
    const me = await fetchMe(accessToken);
    if (!me) return '无法获取用户信息，请稍后重试。';
    setAuth(accessToken, me);
    navigateWithGuard(me);
    return null;
  }

  /** 南哪链路：verify 已带回 user，直接入驻跳转。 */
  function afterNannaAuth(accessToken: string, user: NonNullable<ReturnType<typeof useAuth.getState>['user']>) {
    setAuth(accessToken, user);
    navigateWithGuard(user);
  }

  return (
    <div className="relative h-full w-full overflow-hidden board-immersive">
      <BoardBackdrop />
      <ChalkDoodles />

      <div className="relative z-10 h-full overflow-y-auto flex flex-col items-center justify-center px-4 py-10">
        <Header />
        <TabSwitch tab={tab} onChange={setTab} />

        <div key={tab} className="bb-chalk-in w-full max-w-sm mt-7">
          {tab === 'nanna'
            ? <NannaPanel afterAuth={afterNannaAuth} />
            : <PillPanel afterAuth={afterPillAuth} />}
        </div>

        <p className="mt-8 text-[12px] font-hand tracking-widest chalk-dim" style={{ textShadow: CHALK_TEXT_SHADOW }}>
          校友身份准入 · 由 南哪小帮手 提供
        </p>
      </div>
    </div>
  );
}

/* ------------------------------- header ------------------------------- */

function Header() {
  return (
    <div className="flex flex-col items-center select-none">
      <div className="opacity-95"><LogoMark size={56} /></div>
      <h1
        className="mt-3 font-hand text-[40px] leading-none chalk-bright"
        style={{ textShadow: CHALK_TEXT_SHADOW }}
      >
        南哪小黑板
      </h1>
      <svg width="180" height="12" viewBox="0 0 180 12" fill="none" aria-hidden className="mt-1.5 opacity-90">
        <path
          d="M4 8c22-4.5 44-4.5 66-1.5s40 3.4 62 .5 32-4 44-5.5"
          stroke="#fde68a"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
      <p className="mt-2 text-[13px] font-hand chalk-dim" style={{ textShadow: CHALK_TEXT_SHADOW }}>
        一块大家共享的黑板，进来画一笔吧
      </p>
    </div>
  );
}

/* -------------------------------- tabs -------------------------------- */

function TabSwitch({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const items: { id: Tab; label: string }[] = [
    { id: 'nanna', label: '南哪验证' },
    { id: 'pill', label: '药丸账号' },
  ];
  return (
    <div role="tablist" aria-label="登录方式" className="mt-8 flex items-center gap-10">
      {items.map((it) => {
        const active = tab === it.id;
        return (
          <button
            key={it.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.id)}
            className={`relative pb-2 font-hand text-[19px] transition-colors ${
              active ? 'chalk-bright' : 'chalk-dim hover:opacity-80'
            }`}
            style={{ textShadow: CHALK_TEXT_SHADOW }}
          >
            {it.label}
            {active && (
              <svg width="100%" height="8" viewBox="0 0 100 8" preserveAspectRatio="none" fill="none" aria-hidden className="absolute left-0 -bottom-0.5">
                <path
                  d="M2 5c15-3 30-3 45-1.4s30 2.2 51-.6"
                  stroke="#fde68a"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ----------------------------- chalk atoms ----------------------------- */

function ChalkField({
  id, label, value, onChange, onSubmit, placeholder, type = 'text',
  inputMode, maxLength, autoComplete, disabled, autoFocus, extra,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  extra?: React.ReactNode;
}) {
  return (
    <label htmlFor={id} className="block mb-4">
      <span
        className="block mb-1.5 text-[13px] font-hand chalk-dim"
        style={{ textShadow: CHALK_TEXT_SHADOW }}
      >
        {label}
      </span>
      <span className="relative block">
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit?.(); } }}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          autoComplete={autoComplete}
          disabled={disabled}
          autoFocus={autoFocus}
          className="chalk-input w-full h-12 px-4 rounded-xl font-hand text-[18px] text-white outline-none transition-all duration-150 disabled:opacity-50"
          style={{ textShadow: CHALK_TEXT_SHADOW }}
        />
        {extra != null && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">{extra}</span>
        )}
      </span>
    </label>
  );
}

function ChalkButton({
  onClick, disabled, busy, children, tone = 'default',
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  children: React.ReactNode;
  tone?: 'default' | 'ghost';
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      className={`chalk-btn w-full h-12 rounded-xl font-hand text-[18px] tracking-wider ${
        tone === 'ghost' ? 'chalk-btn-ghost' : ''
      } disabled:opacity-45 disabled:cursor-not-allowed`}
      style={{ textShadow: CHALK_TEXT_SHADOW }}
    >
      {busy ? (
        <span className="inline-flex items-center gap-2.5 justify-center">
          <Spinner /> 处理中…
        </span>
      ) : (
        children
      )}
    </button>
  );
}

function ErrorLine({ msg }: { msg: string }) {
  if (!msg) return null;
  return (
    <div
      role="alert"
      className="bb-chalk-in mb-4 flex items-start gap-2 text-[14px] font-hand text-chalk-red"
      style={{ textShadow: '0 0 2px rgba(248,113,113,0.3)' }}
    >
      <AlertIcon size={16} />
      <span className="leading-5">{msg}</span>
    </div>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".3" strokeWidth="3" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" dur="0.9s" from="0 12 12" to="360 12 12" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

/* --------------------------- countdown hook --------------------------- */

function useCountdown() {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => window.clearInterval(id);
  }, [remaining > 0]);
  return [remaining, setRemaining] as const;
}

/* ------------------------------ 南哪 panel ------------------------------ */

function NannaPanel({ afterAuth }: { afterAuth: (t: string, u: import('@blackboard/shared').User) => void }) {
  const [step, setStep] = useState<NannaStep>('identify');
  const [principal, setPrincipal] = useState('');
  const [code, setCode] = useState('');
  const [challengeUid, setChallengeUid] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useCountdown();
  const [resendIn, setResendIn] = useCountdown();

  const principalType: Principal = useMemo<Principal>(
    () => (principal.includes('@') ? 'email' : 'student_id'),
    [principal]
  );

  async function submitIdentify() {
    setErr('');
    const v = principal.trim();
    if (!v) {
      setErr('请输入邮箱或学号');
      return;
    }
    setBusy(true);
    try {
      const r = await startChallenge(principalType, v);
      if (isErr(r)) {
        if (r.code && BIND_ERROR_CODES.has(r.code)) {
          setStep('guide');
          return;
        }
        setErr(r.error);
        return;
      }
      setChallengeUid(r.challenge_uid);
      setCode('');
      setRemaining(600);
      setResendIn(60);
      setStep('verify');
    } finally {
      setBusy(false);
    }
  }

  async function submitVerify() {
    setErr('');
    if (!/^\d{6,16}$/.test(code.trim())) {
      setErr('请输入收到的数字验证码');
      return;
    }
    setBusy(true);
    try {
      const r = await verifyChallenge(challengeUid, code.trim());
      if (isErr(r)) {
        setErr(r.error);
        if (
          r.code === 'OAUTH_CHALLENGE_EXPIRED' ||
          r.code === 'OAUTH_CHALLENGE_LOCKED' ||
          r.code === 'OAUTH_CHALLENGE_NOT_FOUND' ||
          r.code === 'OAUTH_CHALLENGE_ALREADY_VERIFIED'
        ) {
          setStep('identify');
          setChallengeUid('');
          setRemaining(0);
        }
        return;
      }
      afterAuth(r.accessToken, r.user);
    } finally {
      setBusy(false);
    }
  }

  if (step === 'guide') {
    return (
      <GuideStep
        principal={principal}
        principalType={principalType}
        err={err}
        busy={busy}
        onRetry={async () => {
          setErr('');
          setBusy(true);
          try {
            const r = await startChallenge(principalType, principal.trim());
            if (isErr(r)) {
              if (r.code && BIND_ERROR_CODES.has(r.code)) {
                setErr('似乎还未绑定成功，请确认在公众号内完成了绑定流程。');
                return;
              }
              setErr(r.error);
              setStep('identify');
              return;
            }
            setChallengeUid(r.challenge_uid);
            setCode('');
            setRemaining(600);
            setResendIn(60);
            setStep('verify');
          } finally {
            setBusy(false);
          }
        }}
        onBack={() => { setStep('identify'); setErr(''); }}
      />
    );
  }

  if (step === 'verify') {
    return (
      <div>
        <p className="mb-4 text-[14px] font-hand chalk-dim leading-6" style={{ textShadow: CHALK_TEXT_SHADOW }}>
          验证码已发至 <span className="chalk-bright">{principal}</span> 绑定的微信
          {remaining > 0 && (
            <span className="ml-1">
              （{Math.floor(remaining / 60)}:{(remaining % 60).toString().padStart(2, '0')}）
            </span>
          )}
        </p>
        <ChalkField
          id="nanna-code"
          label="验证码"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 16))}
          onSubmit={submitVerify}
          placeholder="8 位数字"
          autoComplete="one-time-code"
          inputMode="numeric"
          disabled={busy}
          autoFocus
          extra={
            resendIn > 0 ? (
              <span className="px-2 text-[13px] font-hand chalk-dim">{resendIn}s</span>
            ) : (
              <button
                type="button"
                onClick={submitIdentify}
                disabled={busy}
                className="px-2.5 h-9 rounded-lg font-hand text-[14px] chalk-link"
              >
                重新发送
              </button>
            )
          }
        />
        <ErrorLine msg={err} />
        <ChalkButton onClick={submitVerify} busy={busy} disabled={code.length < 6}>
          登录
        </ChalkButton>
        <button
          type="button"
          onClick={() => { setStep('identify'); setErr(''); setCode(''); setRemaining(0); }}
          className="mt-4 mx-auto block font-hand text-[14px] chalk-link"
        >
          ← 换一个邮箱/学号
        </button>
      </div>
    );
  }

  return (
    <div>
      <ChalkField
        id="nanna-principal"
        label="邮箱或学号"
        value={principal}
        onChange={setPrincipal}
        onSubmit={submitIdentify}
        placeholder="邮箱或学号，自动识别"
        autoComplete="username"
        disabled={busy}
        autoFocus
      />
      <ErrorLine msg={err} />
      <ChalkButton onClick={submitIdentify} busy={busy} disabled={!principal.trim()}>
        获取验证码
      </ChalkButton>
      <p className="mt-4 text-center text-[13px] font-hand chalk-dim leading-6" style={{ textShadow: CHALK_TEXT_SHADOW }}>
        南哪小帮手将通过 <span className="chalk-bright">微信</span> 发送 8 位验证码
      </p>
    </div>
  );
}

function GuideStep({
  principal, principalType, err, busy, onRetry, onBack,
}: {
  principal: string;
  principalType: Principal;
  err: string;
  busy: boolean;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <div className="bb-chalk-in">
      <p className="text-center text-[15px] font-hand chalk-bright leading-7" style={{ textShadow: CHALK_TEXT_SHADOW }}>
        还差一步 —— 先关注「南哪小帮手」
      </p>
      <p className="mt-1 mb-5 text-center text-[13px] font-hand chalk-dim" style={{ textShadow: CHALK_TEXT_SHADOW }}>
        <span className="chalk-bright break-all">{principal || '该身份'}</span>{' '}
        还未在南哪绑定
      </p>

      <div className="mx-auto mb-5 w-fit p-3 rounded-xl chalk-card">
        <img
          src="/nanna-qr.jpg"
          alt="南哪小帮手公众号二维码"
          width={168}
          height={168}
          className="rounded-lg block"
        />
        <p className="mt-2.5 text-center text-[13px] font-hand chalk-dim" style={{ textShadow: CHALK_TEXT_SHADOW }}>
          微信扫一扫 · 关注公众号
        </p>
      </div>

      <ol className="mb-5 space-y-1.5 text-[13.5px] font-hand chalk-dim leading-6" style={{ textShadow: CHALK_TEXT_SHADOW }}>
        <li>① 扫码关注「南哪小帮手」公众号</li>
        <li>② 在公众号内按提示完成{principalType === 'email' ? '邮箱' : '学号'}绑定</li>
        <li>③ 回到这里，点下方按钮继续</li>
      </ol>

      <ErrorLine msg={err} />
      <ChalkButton onClick={onRetry} busy={busy}>
        我已绑定，继续
      </ChalkButton>
      <button type="button" onClick={onBack} className="mt-4 mx-auto block font-hand text-[14px] chalk-link">
        ← 换一个邮箱/学号
      </button>
    </div>
  );
}

/* ------------------------------ 药丸 panel ------------------------------ */

function PillPanel({ afterAuth }: { afterAuth: (t: string) => Promise<string | null> }) {
  return (
    <div>
      <PillPassword afterAuth={afterAuth} />
      <p
        className="mt-4 mb-0 text-center text-[13px] font-hand chalk-dim leading-6"
        style={{ textShadow: CHALK_TEXT_SHADOW }}
      >
        仅支持老用户登录，新用户请使用「南哪验证」登录
      </p>
    </div>
  );
}

function PillPassword({ afterAuth }: { afterAuth: (t: string) => Promise<string | null> }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr('');
    const u = username.trim();
    if (!u || !password) {
      setErr('请输入用户名和密码');
      return;
    }
    setBusy(true);
    try {
      const r = await loginPassword(u, password);
      if ('error' in r) {
        setErr(r.error);
        return;
      }
      const e = await afterAuth(r.accessToken);
      if (e) setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <ChalkField
        id="pill-username"
        label="用户名"
        value={username}
        onChange={setUsername}
        onSubmit={submit}
        placeholder="你的用户名"
        autoComplete="username"
        disabled={busy}
        autoFocus
      />
      <ChalkField
        id="pill-password"
        label="密码"
        type="password"
        value={password}
        onChange={setPassword}
        onSubmit={submit}
        placeholder="你的密码"
        autoComplete="current-password"
        disabled={busy}
      />
      <ErrorLine msg={err} />
      <ChalkButton onClick={submit} busy={busy} disabled={!username.trim() || !password}>
        登录
      </ChalkButton>
    </div>
  );
}


/* --------------------------- board backdrop --------------------------- */

type PreviewTheme = 'whiteboard' | 'dark_blackboard' | 'classic_blackboard';
const THEME_BG: Record<PreviewTheme, string> = {
  whiteboard: '#f0f0ec',
  dark_blackboard: '#0a0a0a',
  classic_blackboard: '#1b4a2c',
};

/**
 * 真实涂鸦做背景痕迹：取默认画板最近笔画，低透明度画在黑板上，
 * 像上一批人留下的粉笔印。拿不到就只是干净黑板。
 */
function BoardBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = window.innerWidth;
    c.height = window.innerHeight;
    (async () => {
      try {
        const res = await fetch('/api/public/preview');
        if (!res.ok) return;
        const data = await res.json();
        const theme = (data?.board?.theme ?? 'classic_blackboard') as PreviewTheme;
        if (!data.strokes || data.strokes.length === 0) return;
        const ctx = c.getContext('2d')!;
        let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
        for (const s of data.strokes) {
          x1 = Math.min(x1, s.bbox[0]); y1 = Math.min(y1, s.bbox[1]);
          x2 = Math.max(x2, s.bbox[2]); y2 = Math.max(y2, s.bbox[3]);
        }
        const scale = Math.min(c.width / Math.max(1, x2 - x1), c.height / Math.max(1, y2 - y1)) * 0.72;
        ctx.save();
        ctx.translate(c.width / 2, c.height / 2);
        ctx.scale(scale, scale);
        ctx.translate(-(x1 + x2) / 2, -(y1 + y2) / 2);
        for (const s of data.strokes) {
          const points: Point[] = s.points.map((p: any) => ({ x: p[0], y: p[1], pressure: p[2] }));
          renderStroke(ctx, { tool: s.tool, color: s.color, size: s.size * 2, points });
        }
        ctx.restore();
      } catch {
        /* 干净黑板也很好 */
      }
    })();
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden
      className="absolute inset-0 z-0 pointer-events-none opacity-[0.13]"
    />
  );
}

/** 四角粉笔小涂鸦，手机上隐藏。 */
function ChalkDoodles() {
  return (
    <div aria-hidden className="absolute inset-0 z-0 pointer-events-none select-none hidden sm:block">
      <svg className="absolute left-[7%] top-[16%] opacity-20" width="90" height="90" viewBox="0 0 90 90" fill="none">
        <path d="M45 12l5.5 18L69 35l-18 5-6 18-5.5-18L21 35l18.5-5z" stroke="#fff" strokeWidth="2.4" strokeLinejoin="round" />
        <circle cx="70" cy="66" r="2.6" fill="#fde68a" />
      </svg>
      <svg className="absolute right-[9%] top-[22%] opacity-20" width="110" height="70" viewBox="0 0 110 70" fill="none">
        <path d="M6 52c10-34 34-44 48-30s-2 34 14 38 30-14 34-34" stroke="#fde68a" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
      <svg className="absolute left-[12%] bottom-[14%] opacity-[0.15]" width="120" height="60" viewBox="0 0 120 60" fill="none">
        <path d="M8 40c14 6 30 8 46 4s26-14 34-28" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M88 16l4 8 8 2" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg className="absolute right-[11%] bottom-[18%] opacity-[0.16]" width="80" height="80" viewBox="0 0 80 80" fill="none">
        <circle cx="34" cy="40" r="16" stroke="#fff" strokeWidth="2.2" />
        <path d="M50 40h22M62 32v16" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </div>
  );
}
